import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SocialConnect = () => {
  const socialPlatforms = [
    {
      name: 'LinkedIn',
      handle: '@alexdeveloper',
      description: 'Professional updates, industry insights, and career highlights',
      icon: 'Linkedin',
      color: 'bg-blue-600',
      hoverColor: 'hover:bg-blue-700',
      url: 'https://linkedin.com/in/alexdeveloper',
      followers: '2.5K'
    },
    {
      name: 'GitHub',
      handle: '@alexdev',
      description: 'Open source projects, code repositories, and contributions',
      icon: 'Github',
      color: 'bg-gray-800',
      hoverColor: 'hover:bg-gray-900',
      url: 'https://github.com/alexdev',
      followers: '1.2K'
    },
    {
      name: 'Twitter',
      handle: '@alexcodes',
      description: 'Development tips, tech thoughts, and community discussions',
      icon: 'Twitter',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      url: 'https://twitter.com/alexcodes',
      followers: '890'
    },
    {
      name: 'Dev.to',
      handle: '@alexdeveloper',
      description: 'Technical articles, tutorials, and development insights',
      icon: 'FileText',
      color: 'bg-gray-900',
      hoverColor: 'hover:bg-black',
      url: 'https://dev.to/alexdeveloper',
      followers: '650'
    }
  ];

  const communityInvolvement = [
    {
      title: 'React Meetup Speaker',
      description: 'Regular speaker at local React meetups and conferences',
      icon: 'Users',
      color: 'text-accent'
    },
    {
      title: 'Open Source Contributor',
      description: 'Active contributor to popular React and Node.js libraries',
      icon: 'GitBranch',
      color: 'text-success'
    },
    {
      title: 'Tech Mentor',
      description: 'Mentoring junior developers through various platforms',
      icon: 'GraduationCap',
      color: 'text-warning'
    },
    {
      title: 'Code Reviewer',
      description: 'Providing code reviews and technical guidance to teams',
      icon: 'Search',
      color: 'text-error'
    }
  ];

  const handleSocialClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4">
            Let's Connect
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Follow my journey, see my latest work, and join the conversation across different platforms.
          </p>
        </div>

        {/* Social Platforms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {socialPlatforms?.map((platform, index) => (
            <div key={index} className="bg-card border border-border rounded-xl p-6 hover:shadow-medium transition-all duration-300 group">
              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 ${platform?.color} ${platform?.hoverColor} rounded-lg flex items-center justify-center transition-colors duration-300`}>
                  <Icon name={platform?.icon} size={24} color="white" />
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-text-primary">
                      {platform?.name}
                    </h3>
                    <span className="text-sm text-text-secondary">
                      {platform?.followers} followers
                    </span>
                  </div>
                  
                  <p className="text-accent font-medium mb-2">
                    {platform?.handle}
                  </p>
                  
                  <p className="text-text-secondary text-sm mb-4">
                    {platform?.description}
                  </p>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="right"
                    iconSize={14}
                    onClick={() => handleSocialClick(platform?.url)}
                    className="group-hover:border-accent group-hover:text-accent"
                  >
                    Follow
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Community Involvement */}
        <div className="bg-card border border-border rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              Community Involvement
            </h3>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Beyond individual projects, I'm actively involved in the developer community through various initiatives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {communityInvolvement?.map((activity, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-muted/30 rounded-lg">
                <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name={activity?.icon} size={20} className={activity?.color} />
                </div>
                <div>
                  <h4 className="font-semibold text-text-primary mb-2">
                    {activity?.title}
                  </h4>
                  <p className="text-text-secondary text-sm">
                    {activity?.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20 rounded-2xl p-8 lg:p-12 text-center">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Mail" size={32} className="text-accent" />
            </div>
            
            <h3 className="text-2xl font-bold text-text-primary mb-4">
              Stay Updated
            </h3>
            
            <p className="text-text-secondary mb-8">
              Get monthly insights on web development, project updates, and industry trends. 
              No spam, just valuable content for developers and tech enthusiasts.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
              />
              <Button
                variant="default"
                iconName="Send"
                iconPosition="right"
                className="bg-accent hover:bg-hover text-accent-foreground whitespace-nowrap"
              >
                Subscribe
              </Button>
            </div>

            <p className="text-xs text-text-secondary mt-4">
              Join 500+ developers who get my monthly newsletter. Unsubscribe anytime.
            </p>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-text-primary mb-4">
            Ready to Start a Project?
          </h3>
          <p className="text-text-secondary mb-8 max-w-xl mx-auto">
            Whether you found me through social media or a referral, I'd love to hear about your project ideas.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <Button
              variant="default"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              onClick={() => document.getElementById('contact-forms')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-accent hover:bg-hover text-accent-foreground"
            >
              Get In Touch
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={() => document.getElementById('calendar-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Schedule a Call
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialConnect;